import * as React from "react";
import * as Safe from "safe-portals";
import { SafeQueryParser, makeSafeQueryParser } from "../../lib/safe_query_parser";

/*
 * Methods to allow core and plugins to register frontend routes which are member facing
 * and use React.
 * i.e. a plugin might want a fixed page where members can go and submit a response from a target
 * It is heavily based on FrontendAdminRoute but with much simpler args (for now).
 */

type RouteComponent<T> = React.FC<T> | React.ComponentClass<T>;
export interface MemberRouteArgs {
  rawRoute: string;
}

interface FrontendMemberRoute<T> {
  urlPrefix: string;
  match: (url: string, memberRouteArgs: MemberRouteArgs) => undefined | any;
}

const allRoutes: FrontendMemberRoute<any>[] = [];

export function getAllRoutes(): ReadonlyArray<FrontendMemberRoute<any>> {
  return allRoutes;
}

export function setUrl(route: string, args: Record<string, string>): void {
  const params = new URLSearchParams(args).toString();

  window.location.hash = params ? `${route}?${params}` : route;
}

export function createFrontendMemberRoute<T>(props: {
  urlPrefix: string;
  args: Safe.Obj<T>;
  component: RouteComponent<T & MemberRouteArgs>;
}): FrontendMemberRoute<T> {
  const parser: SafeQueryParser<T> = makeSafeQueryParser(props.args);

  const getUrlParams = (url: string, urlPrefix: string): T | "NoMatch" => {
    if (url.startsWith(urlPrefix + "?")) {
      // arguments after '?'
      return parser.fromQueryString(url.slice(urlPrefix.length + 1));
    } else if (url === urlPrefix) {
      // no arguments. perhaps we can match a 'no arguments' route
      return parser.fromQueryString("");
    } else {
      // no match
      return "NoMatch";
    }
  };

  const route: FrontendMemberRoute<T> = {
    urlPrefix: props.urlPrefix,
    match: (url: string, memberRouteArgs: MemberRouteArgs): any => {
      const args: T | "NoMatch" = getUrlParams(url, props.urlPrefix);

      if (args === "NoMatch") {
        return undefined;
      } else {
        return React.createElement(props.component, {
          ...args,
          ...memberRouteArgs,
        });
      }
    },
  };

  allRoutes.push(route);

  return route;
}
